import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Zap, MessageSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Play a short, non-intrusive UI chime (~350 ms). Uses Web Audio directly
 * so we don't ship an audio asset. Two soft sine tones (A5 → E6) crossfaded
 * for a "premium ping" feel, capped low enough to avoid startling the user.
 */
function playInAppChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const tone = (freq, offset, dur, gain = 0.14) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(freq, now + offset);
      g.gain.setValueAtTime(0, now + offset);
      g.gain.linearRampToValueAtTime(gain, now + offset + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + offset + dur);
      o.connect(g).connect(ctx.destination);
      o.start(now + offset);
      o.stop(now + offset + dur + 0.05);
    };
    tone(880, 0, 0.22);
    tone(1318.5, 0.12, 0.28);
  } catch (_) { /* silent — no sound is better than a crash */ }
}

const POLL_MS = 15000; // Poll every 15 s for new in-app messages.

/**
 * Renders as a floating overlay that polls `/inapp/pending` and displays
 * pending messages one at a time. Two kinds are supported:
 *
 *  - `admin_message`: plain admin pop-up with a single "OK" action.
 *  - `upcharge_offer`: booking-specific surcharge acceptance (client
 *    only). Accepts triggers a `POST /client/bookings/:id/accept-upcharge`.
 *
 * Rendered globally in `App.js` so any authenticated user across roles
 * (client / hotel / driver) can receive admin pop-ups.
 */
export default function InAppMessageOverlay() {
  const { profile } = useAuth();
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);

  const authed = !!profile?.id;

  const fetchPending = useCallback(async () => {
    if (!authed) return;
    try {
      const r = await api.get("/inapp/pending");
      const msgs = r.data?.messages || [];
      if (msgs.length) setQueue((prev) => mergeUnique(prev, msgs));
    } catch (_) { /* silent */ }
  }, [authed]);

  useEffect(() => {
    if (!authed) return;
    fetchPending();
    const id = setInterval(fetchPending, POLL_MS);
    return () => clearInterval(id);
  }, [authed, fetchPending]);

  const current = queue[0];
  const isUpcharge = current?.kind === "upcharge_offer";
  const isStripeLink = current?.kind === "stripe_onboarding_link";

  // Play a soft chime + haptic tap once, when a new message becomes
  // the visible one at the top of the queue.
  const lastPlayedIdRef = React.useRef(null);
  useEffect(() => {
    if (!current) return;
    if (lastPlayedIdRef.current === current.id) return;
    lastPlayedIdRef.current = current.id;
    playInAppChime();
    try {
      if (navigator.vibrate) navigator.vibrate([30, 40, 30]);
    } catch (_) { /* ignore */ }
  }, [current?.id]);

  const dismiss = async () => {
    if (!current) return;
    setBusy(true);
    try {
      await api.post(`/inapp/${current.id}/ack`);
    } catch (_) { /* silent */ }
    setQueue((prev) => prev.slice(1));
    setBusy(false);
  };

  const acceptUpcharge = async () => {
    if (!current) return;
    setBusy(true);
    try {
      const r = await api.post(`/client/bookings/${current.booking_id}/accept-upcharge`, {
        amount: current.amount,
      });
      toast.success(`Merci ! Nouveau prix : ${r.data?.new_price?.toFixed(2)} €`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
    setQueue((prev) => prev.slice(1));
    setBusy(false);
  };

  if (!current) return null;

  return (
    <Dialog open={true} onOpenChange={() => dismiss()}>
      <DialogContent
        className="max-w-sm animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-4 duration-500 ease-out"
        data-testid="inapp-message-overlay"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isUpcharge ? (
              <><Zap className="h-5 w-5 text-primary animate-pulse" /> Prise en charge plus rapide</>
            ) : (
              <><MessageSquare className="h-5 w-5 text-primary" /> {current.title || "Message Price"}</>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {isUpcharge ? (
            <>
              <p className="text-sm text-muted-foreground">
                {current.message || `Ajoute +${Number(current.amount || 0).toFixed(0)} € pour attirer un chauffeur plus rapidement. On rediffuse ta course immédiatement.`}
              </p>
              <div className="rounded-xl bg-primary/10 p-4 text-center">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Supplément</p>
                <p className="mt-1 font-[Outfit] text-3xl font-semibold text-primary" data-testid="inapp-upcharge-amount">
                  +{Number(current.amount || 0).toFixed(0)} €
                </p>
              </div>
            </>
          ) : (
            <p className="text-sm">{current.body}</p>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          {isUpcharge && (
            <Button variant="outline" onClick={dismiss} disabled={busy} data-testid="inapp-decline">
              Non merci
            </Button>
          )}
          {isStripeLink && current.url ? (
            <>
              <Button variant="outline" onClick={dismiss} disabled={busy} data-testid="inapp-dismiss">
                Plus tard
              </Button>
              <Button
                onClick={async () => { window.open(current.url, "_blank", "noopener,noreferrer"); await dismiss(); }}
                disabled={busy}
                className="bg-primary hover:bg-primary/90"
                data-testid="inapp-open-stripe"
              >
                Ouvrir Stripe
              </Button>
            </>
          ) : (
            <Button
              onClick={isUpcharge ? acceptUpcharge : dismiss}
              disabled={busy}
              className="bg-primary hover:bg-primary/90"
              data-testid={isUpcharge ? "inapp-accept-upcharge" : "inapp-ack"}
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isUpcharge ? `Accepter +${Number(current.amount || 0).toFixed(0)} €` : "OK"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function mergeUnique(existing, incoming) {
  const seen = new Set(existing.map((m) => m.id));
  return [...existing, ...incoming.filter((m) => !seen.has(m.id))];
}
